import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost } from '../../../../src/api/client';
import Card from '../../../../src/components/Card';
import Button from '../../../../src/components/Button';
import Input from '../../../../src/components/Input';
import FreeUpsellPicker from '../../../../src/components/upsells/FreeUpsellPicker';
import type { UpsellCatalogItem } from '../../../../src/components/upsells/UpsellPolicyPicker';
import Picker from '../../../../src/components/Picker';
import QrSvgView from '../../../../src/components/QrSvgView';
import { useToast } from '../../../../src/components/Toast';
import ContentContainer from '../../../../src/components/ContentContainer';
import { useTheme } from '../../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../../src/utils/theme';

interface Location { id: string; name: string }
interface WashTier { id: string; name: string; priceInCents: number; displayOrder: number; isActive: boolean }

type Mode = 'single' | 'multi';

/**
 * CreateGiftQRScreen — owner-facing form to mint a gift-QR voucher.
 *
 * Two modes:
 *   • single — one customer ever can redeem (redemption_limit = 1)
 *   • multi  — up to N total redemptions, "1 per customer" enforced via
 *              per_customer_limit on top of the redemption_limit
 *
 * After creation, the owner sees a labeled QR they can print or share.
 */
export default function CreateGiftQRScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [washTiers, setWashTiers] = useState<WashTier[]>([]);

  const [mode, setMode] = useState<Mode>('multi');
  const [discountKind, setDiscountKind] = useState<'free_wash' | 'percent' | 'free_minutes' | 'free_vend' | 'credit' | 'free_upsell'>('percent');
  const [percentOff, setPercentOff] = useState('25');
  // 1.4 R05: percent promos cover the wash only or wash + add-ons; wash promos can give add-ons away free.
  const [discountScope, setDiscountScope] = useState<'wash' | 'wash_and_upsells'>('wash');
  const [freeUpsellIds, setFreeUpsellIds] = useState<string[]>([]);
  const [upsellCatalog, setUpsellCatalog] = useState<UpsellCatalogItem[]>([]);
  // Free vending credit in dollars ($0.25–$100 → freeVendCents).
  const [freeVendAmount, setFreeVendAmount] = useState('2');
  // Free minutes (1–240) — scope is self-serve and/or vacuums ONLY (mirrors gifts/new).
  const [freeMinutes, setFreeMinutes] = useState('10');
  const [minutesSelfServe, setMinutesSelfServe] = useState(true);
  const [minutesVacuum, setMinutesVacuum] = useState(false);
  // Gift credit in dollars ($1–$500 → creditCents) — deposited instantly at claim.
  const [creditAmount, setCreditAmount] = useState('10');
  // Multi-select applicability: wash tiers + self-serve + vacuums + vending.
  // Defaults (all tiers checked, rest unchecked) preserve the old
  // automatic-only behavior. Backend rules enforced client-side:
  //   free_wash    → needs ≥1 wash tier; never vacuums/vending
  //   free_minutes → self-serve and/or vacuums only (no tiers, no vending)
  //   free_vend    → vending only (scope forced, checkboxes hidden)
  //   credit       → scope-free (balance dollars, spendable anywhere)
  //   percent      → any combination
  const [selectedTierIds, setSelectedTierIds] = useState<string[]>([]);
  const [selfServe, setSelfServe] = useState(false);
  const [vacuum, setVacuum] = useState(false);
  const [vending, setVending] = useState(false);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string>('any');
  const [totalCap, setTotalCap] = useState('50');
  const [expiresInDays, setExpiresInDays] = useState('30');

  const [createdSvg, setCreatedSvg] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const locs = await apiGet<Location[]>('/api/locations');
        if (ac.signal.aborted) return;
        setLocations(locs);
        const catalog = await apiGet<WashTier[]>('/api/wash-tiers');
        if (ac.signal.aborted) return;
        const active = catalog.filter(t => t.isActive).sort((a, b) => a.displayOrder - b.displayOrder);
        setWashTiers(active);
        setSelectedTierIds(active.map(t => t.id));
        apiGet<UpsellCatalogItem[]>('/api/upsells').then(list => { if (!ac.signal.aborted) setUpsellCatalog(list.filter(u => u.isActive !== false)); }).catch(() => {});
      } catch (err: any) {
        if (!ac.signal.aborted) toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    })();
    return () => ac.abort();
  }, []);

  const handleSubmit = async () => {
    const limit = mode === 'single' ? 1 : Math.max(2, parseInt(totalCap) || 50);
    if (mode === 'multi' && (!totalCap || parseInt(totalCap) <= 1)) {
      toast.show({ type: 'error', title: 'Error', message: 'Multi-use cap must be 2 or more' });
      return;
    }
    if (discountKind === 'percent') {
      const pct = parseInt(percentOff);
      if (!pct || pct < 1 || pct > 100) {
        toast.show({ type: 'error', title: 'Error', message: 'Percent must be 1–100' });
        return;
      }
    }
    const days = parseInt(expiresInDays);
    if (!days || days < 1) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter valid expiration days' });
      return;
    }
    if (discountKind === 'free_vend') {
      const cents = Math.round((parseFloat(freeVendAmount) || 0) * 100);
      if (cents < 25 || cents > 10000) {
        toast.show({ type: 'error', title: 'Error', message: 'Vending credit must be $0.25–$100' });
        return;
      }
    } else if (discountKind === 'free_minutes') {
      const mins = parseInt(freeMinutes);
      if (!mins || mins < 1 || mins > 240) {
        toast.show({ type: 'error', title: 'Error', message: 'Free minutes must be 1–240' });
        return;
      }
      if (!minutesSelfServe && !minutesVacuum) {
        setScopeError('Pick self-serve bays, vacuums, or both.');
        return;
      }
    } else if (discountKind === 'credit') {
      const cents = Math.round((parseFloat(creditAmount) || 0) * 100);
      if (cents < 100 || cents > 50000) {
        toast.show({ type: 'error', title: 'Error', message: 'Credit must be $1–$500' });
        return;
      }
    } else {
      if (selectedTierIds.length === 0 && !selfServe && !vacuum && !vending) {
        setScopeError('Select at least one wash type the QR applies to.');
        return;
      }
      if (discountKind === 'free_wash' && selectedTierIds.length === 0) {
        setScopeError('A free wash needs at least one wash tier selected.');
        return;
      }
      if (discountKind === 'free_upsell' && freeUpsellIds.length === 0) {
        setScopeError('Pick at least one free add-on.');
        return;
      }
    }

    try {
      setSubmitting(true);
      const body: any = {
        kind: 'promo',
        discountKind,
        locationId: locationId === 'any' ? null : locationId,
        redemptionLimit: limit,
        perCustomerLimit: 1,
        expiresInDays: days,
        source: mode === 'single' ? 'gift_qr_single' : 'gift_qr_multi',
      };
      if (discountKind === 'free_vend') {
        // Vending-only scope is mandatory server-side for free_vend.
        body.freeVendCents = Math.round((parseFloat(freeVendAmount) || 0) * 100);
        body.appliesTo = { tierIds: [], selfServe: false, vending: true };
      } else if (discountKind === 'free_minutes') {
        // appliesTo carries the self-serve/vacuum split (no tiers, no vending).
        body.freeMinutes = parseInt(freeMinutes);
        body.appliesTo = { tierIds: [], selfServe: minutesSelfServe, vacuum: minutesVacuum };
      } else if (discountKind === 'credit') {
        // Scope-free: balance dollars deposited at claim, spendable anywhere.
        // Credit QRs are never email-bound and never location-scoped.
        body.creditCents = Math.round((parseFloat(creditAmount) || 0) * 100);
        body.locationId = null;
      } else {
        body.percentOff = discountKind === 'percent' ? parseInt(percentOff) : discountKind === 'free_wash' ? 100 : undefined;
        if (discountKind === 'percent') body.discountScope = discountScope;
        if (freeUpsellIds.length && discountKind !== 'percent') body.freeUpsellIds = freeUpsellIds;
        // free_wash never includes vacuums/vending (backend rejects them).
        body.appliesTo = {
          tierIds: selectedTierIds,
          selfServe,
          vacuum: discountKind === 'percent' ? vacuum : false,
          vending: discountKind === 'percent' ? vending : false,
        };
      }
      const created = await apiPost<{ id: string; code: string }>('/api/vouchers', body);

      // Fetch the labeled QR SVG for display
      const qr = await apiGet<{ svg: string; redemptionUrl: string; code: string }>(`/api/vouchers/${created.id}/qr`);
      setCreatedSvg(qr.svg);
      setCreatedUrl(qr.redemptionUrl);
      setCreatedCode(qr.code);
      toast.show({ type: 'success', title: 'QR created' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTier = (id: string) => {
    setScopeError(null);
    setSelectedTierIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelfServe = () => { setScopeError(null); setSelfServe(v => !v); };
  const toggleVacuum = () => { setScopeError(null); setVacuum(v => !v); };
  const toggleVending = () => { setScopeError(null); setVending(v => !v); };

  const locationOptions = [{ label: 'Any location', value: 'any' }, ...locations.map(l => ({ label: l.name, value: l.id }))];

  // After creation: show the QR with a Done button.
  if (createdSvg) {
    return (
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer>
          <Card style={[styles.formCard, { alignItems: 'center', padding: spacing.lg }]}>
            <Text style={styles.sectionTitle}>Your QR is ready</Text>
            <View style={{ alignItems: 'center', marginVertical: spacing.md }}>
              <QrSvgView uri={createdSvg} width={320} accessibilityLabel="Gift QR code" />
            </View>
            <Text style={styles.hintText}>Code: {createdCode}</Text>
            <Text style={[styles.hintText, { fontSize: fontSize.xs }]}>Link: {createdUrl}</Text>
            <Button
              title="Done"
              onPress={() => router.back()}
              size="lg"
              style={{ marginTop: spacing.lg, width: '100%' }}
            />
          </Card>
          <View style={{ height: spacing.xxl }} />
        </ContentContainer>
      </ScrollView>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>QR Type</Text>
          <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm }}>
            {(['single', 'multi'] as Mode[]).map(m => (
              <TouchableOpacity
                key={m}
                onPress={() => setMode(m)}
                style={[styles.chip, mode === m && { backgroundColor: colors.primary, borderColor: colors.primary }]}
              >
                <Text style={[styles.chipText, mode === m && { color: '#fff' }]}>
                  {m === 'single' ? 'Single-use' : 'Multi-use (1 per customer)'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          {mode === 'multi' && (
            <Input label="Total redemptions allowed" value={totalCap} onChangeText={setTotalCap} placeholder="50" keyboardType="number-pad" />
          )}
        </Card>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Discount</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
            {([['percent', 'Percent off'], ['free_wash', 'Free wash'], ['free_upsell', 'Free add-on with a wash'], ['free_minutes', 'Free minutes'], ['free_vend', 'Free vending credit'], ['credit', 'Credit $']] as const).map(([k, label]) => (
              <TouchableOpacity
                key={k}
                onPress={() => { setScopeError(null); setDiscountKind(k); }}
                style={[styles.chip, discountKind === k && { backgroundColor: colors.primary, borderColor: colors.primary }]}
              >
                <Text style={[styles.chipText, discountKind === k && { color: '#fff' }]}>{label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {discountKind === 'percent' && (
            <>
              <Input label="Percent off (1–100)" value={percentOff} onChangeText={setPercentOff} placeholder="25" keyboardType="number-pad" />
              <Text style={styles.sectionTitle}>Discount covers</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
                {([['wash', 'The wash only'], ['wash_and_upsells', 'The wash and upsells']] as const).map(([v, label]) => (
                  <TouchableOpacity key={v} onPress={() => setDiscountScope(v)} style={[styles.chip, discountScope === v && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                    <Text style={[styles.chipText, discountScope === v && { color: '#fff' }]}>{label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}
          {(discountKind === 'free_wash' || discountKind === 'free_upsell') && (
            <>
              {discountKind === 'free_upsell' && <Text style={styles.hintText}>The customer pays for the wash and gets the upsells below free. Pick which wash tiers it works on.</Text>}
              <FreeUpsellPicker catalog={upsellCatalog} selected={freeUpsellIds} onChange={ids => { setScopeError(null); setFreeUpsellIds(ids); }} title={discountKind === 'free_upsell' ? 'Free upsells' : 'Also free with this wash (optional)'} />
            </>
          )}
          {discountKind === 'free_minutes' && (
            <Input label="Free minutes (1–240)" value={freeMinutes} onChangeText={setFreeMinutes} placeholder="10" keyboardType="number-pad" />
          )}
          {discountKind === 'free_vend' && (
            <>
              <Input
                label="Vending credit ($0.25–$100)"
                value={freeVendAmount}
                onChangeText={setFreeVendAmount}
                placeholder="2"
                keyboardType="decimal-pad"
              />
              <Text style={styles.hintText}>
                Redeemable at your vending machines only — the credit is pushed to the machine like coins.
              </Text>
            </>
          )}
          {discountKind === 'credit' && (
            <>
              <Input
                label="Gift credit ($1–$500)"
                value={creditAmount}
                onChangeText={setCreditAmount}
                placeholder="10"
                keyboardType="decimal-pad"
              />
              <Text style={styles.hintText}>
                Each customer who scans and claims gets ${(Math.round((parseFloat(creditAmount) || 0) * 100) / 100).toFixed(2)} of gift credit instantly, up to the redemption limit.
              </Text>
            </>
          )}
        </Card>

        {/* Credit is balance dollars spendable anywhere — no scope card. */}
        {discountKind !== 'credit' && (
        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Applies To</Text>
          {discountKind === 'free_vend' ? (
            <Text style={[styles.hintText, { textAlign: 'left', marginBottom: spacing.sm }]}>
              Vending machines only — free vending credit can't apply to washes or vacuums.
            </Text>
          ) : discountKind === 'free_minutes' ? (
            <>
              <TouchableOpacity style={styles.checkRow} onPress={() => { setScopeError(null); setMinutesSelfServe(v => !v); }}>
                <View style={[styles.checkbox, minutesSelfServe && styles.checkboxActive]}>
                  {minutesSelfServe && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Self-serve bays</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.checkRow} onPress={() => { setScopeError(null); setMinutesVacuum(v => !v); }}>
                <View style={[styles.checkbox, minutesVacuum && styles.checkboxActive]}>
                  {minutesVacuum && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Vacuums</Text>
              </TouchableOpacity>
              <Text style={[styles.hintText, { textAlign: 'left' }]}>
                Free minutes work at self-serve bays and vacuums only — wash tiers and vending aren't eligible.
              </Text>
            </>
          ) : (
            <>
              {washTiers.map(t => (
                <TouchableOpacity key={t.id} style={styles.checkRow} onPress={() => toggleTier(t.id)}>
                  <View style={[styles.checkbox, selectedTierIds.includes(t.id) && styles.checkboxActive]}>
                    {selectedTierIds.includes(t.id) && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                  <Text style={styles.checkLabel}>{t.name} (${(t.priceInCents / 100).toFixed(2)})</Text>
                </TouchableOpacity>
              ))}
              <TouchableOpacity style={styles.checkRow} onPress={toggleSelfServe}>
                <View style={[styles.checkbox, selfServe && styles.checkboxActive]}>
                  {selfServe && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Self-serve bays</Text>
              </TouchableOpacity>
              {/* Vacuums + Vending: percent-off only — free_wash can't apply
                  there (backend rejects), so the boxes disable with a hint. */}
              <TouchableOpacity
                style={[styles.checkRow, discountKind !== 'percent' && { opacity: 0.4 }]}
                onPress={toggleVacuum}
                disabled={discountKind !== 'percent'}
              >
                <View style={[styles.checkbox, discountKind === 'percent' && vacuum && styles.checkboxActive]}>
                  {discountKind === 'percent' && vacuum && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Vacuums</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.checkRow, discountKind !== 'percent' && { opacity: 0.4 }]}
                onPress={toggleVending}
                disabled={discountKind !== 'percent'}
              >
                <View style={[styles.checkbox, discountKind === 'percent' && vending && styles.checkboxActive]}>
                  {discountKind === 'percent' && vending && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Vending machines</Text>
              </TouchableOpacity>
              {discountKind === 'free_wash' && (
                <Text style={[styles.hintText, { textAlign: 'left' }]}>
                  Free washes apply to wash tiers only — use Free vending credit for vending machines.
                </Text>
              )}
            </>
          )}
          {scopeError && <Text style={styles.errorText}>{scopeError}</Text>}
          <Picker label="Valid at" options={locationOptions} value={locationId} onValueChange={setLocationId} />
        </Card>
        )}

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Expiration</Text>
          <Input label="Expires in (days)" value={expiresInDays} onChangeText={setExpiresInDays} placeholder="30" keyboardType="number-pad" />
        </Card>

        <Button title="Create QR" onPress={handleSubmit} loading={submitting} size="lg" style={{ marginTop: spacing.md }} />
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  formCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  hintText: { fontSize: fontSize.sm, color: c.textLight, marginTop: spacing.xs, textAlign: 'center' },
  chip: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  chipText: { fontSize: fontSize.sm, color: c.text },
  checkRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm },
  checkbox: { width: 20, height: 20, borderRadius: 4, borderWidth: 2, borderColor: c.border, justifyContent: 'center', alignItems: 'center' },
  checkboxActive: { backgroundColor: c.primary, borderColor: c.primary },
  checkmark: { color: c.white, fontSize: 12, fontWeight: fontWeight.bold as any },
  checkLabel: { fontSize: fontSize.sm, color: c.text, marginLeft: spacing.sm, flexShrink: 1 },
  errorText: { fontSize: fontSize.sm, color: c.danger, marginBottom: spacing.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
